import React, {FC, useRef} from 'react';
import styled, {css} from 'styled-components';

import {DraftMessageModel} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  ApplicationsPanelId,
  ComposerPanelId,
  ViewerPanelId,
} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch} from '../../../../../helpers/appReduxHelpers';
import {ApplicationComposerToolbarEntryPointModel} from '../../../../../models/api/applicationEntryPointModel';
import {ApplicationModel} from '../../../../../models/api/applicationModel';
import {popoutApplicationAction} from '../../../../../store/panels/applications/applicationPanelActions';
import {
  ApplicationPanelLocationsEnum,
  ApplicationsPanelState,
} from '../../../../../store/panels/applications/applicationPanelState';
import {removePanelAction} from '../../../../../store/panels/panelsActions';
import {ApplicationComposerEntryPointPanel} from './applicationComposerEntryPointPanel';

/*
 * Props.
 */

interface ApplicationComposerEntryPointButtonProps {
  entryPoint: ApplicationComposerToolbarEntryPointModel;
  application: ApplicationModel;
  panel?: ApplicationsPanelState;
  panelId: ComposerPanelId | ViewerPanelId;
  cellsContext: CellsContext | undefined;
  draftMessage: DraftMessageModel | undefined;
  viewerPanelId: ViewerPanelId | undefined;
}

/*
 * Style.
 */

const StyledButtonImg = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 9000px;
`;

interface StyledImgButtonWrapperDivProps {
  $hasHover: boolean;
}

const StyledImgButtonWrapperDiv = styled.div<StyledImgButtonWrapperDivProps>`
  display: flex;
  justify-content: center;
  padding: 5px;
  border-radius: 8px;
  margin: 0 3px;
  width: 30px;

  ${(p) =>
    p.$hasHover &&
    css`
      background-color: ${p.theme.alphas.gray10};
    `}
  ${(p) => css`
    &:hover {
      background-color: ${p.theme.alphas.gray10};
    }
  `}
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  min-width: 36px;
`;

const StyledIconButtonWrapperDiv = styled.div`
  min-width: 36px;
  display: flex;
  justify-content: center;
`;

/*
 * Component.
 */

export const ApplicationComposerEntryPointButton: FC<ApplicationComposerEntryPointButtonProps> = (props) => {
  const {panelId, entryPoint, application, panel, cellsContext, draftMessage, viewerPanelId} = props;
  const buttonRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const theme = useUserTheme();

  const openComposerPanel = () => {
    dispatch(
      popoutApplicationAction(entryPoint, {
        location: ApplicationPanelLocationsEnum.POPOVER,
        parentPanelId: panelId,
      }),
    );
  };

  const onRequestClose = (id: ApplicationsPanelId) => {
    dispatch(removePanelAction(id));
  };

  const renderButton = () => {
    if (application.iconUrl) {
      return (
        <StyledTooltipCoordinator render={() => entryPoint.name}>
          <ChromelessButton ref={buttonRef} onClick={openComposerPanel} hasHover={Boolean(panel)}>
            <StyledImgButtonWrapperDiv $hasHover={Boolean(panel)}>
              <StyledButtonImg src={application.iconUrl} />
            </StyledImgButtonWrapperDiv>
          </ChromelessButton>
        </StyledTooltipCoordinator>
      );
    }
    return (
      <StyledIconButtonWrapperDiv>
        <IconButton
          ref={buttonRef}
          iconName="integration"
          label={entryPoint.name}
          onClick={openComposerPanel}
          hasHover={Boolean(panel)}
          color={theme.greys.shade80}
          hoverColor={theme.greys.shade80}
        />
      </StyledIconButtonWrapperDiv>
    );
  };

  if (!draftMessage) {
    return null;
  }

  return (
    <>
      {renderButton()}
      {panel && (
        <ApplicationComposerEntryPointPanel
          panelId={panel.id}
          frame={panel.frame as ApplicationComposerToolbarEntryPointModel}
          anchor={buttonRef}
          onRequestClose={() => onRequestClose(panel.id)}
          cellsContext={cellsContext}
          draftMessage={draftMessage}
          viewerPanelId={viewerPanelId}
        />
      )}
    </>
  );
};
